import React from 'react'
import { ShoppingBag, FileText, Shield } from 'lucide-react'
import { useLocalization } from '../../hooks/useLocalization'

/**
 * Module Overview Section Component
 * 
 * Displays the three main modules: Marketplace, Enterprise, and Government
 */
const ModuleOverviewSection: React.FC = () => {
  const { t, translations } = useLocalization()

  const modules = [
    {
      icon: ShoppingBag,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900',
      key: 'marketplace'
    },
    {
      icon: FileText,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900',
      key: 'enterprise'
    },
    {
      icon: Shield,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900',
      key: 'government'
    }
  ]

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {t('home.partnership.modules.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t('home.partnership.modules.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {modules.map((module) => {
            const Icon = module.icon
            const moduleData = translations.home.partnership.modules[module.key as 'marketplace' | 'enterprise' | 'government']
            const features = moduleData?.features || []
            
            return (
              <div
                key={module.key}
                className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className={`w-16 h-16 ${module.bgColor} rounded-lg flex items-center justify-center mb-6`}>
                  <Icon className={`w-8 h-8 ${module.iconColor}`} />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">
                  {t(`home.partnership.modules.${module.key}.title`)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {t(`home.partnership.modules.${module.key}.description`)}
                </p>
                <ul className="space-y-2">
                  {Array.isArray(features) && features.map((feature: string, index: number) => (
                    <li key={index} className="flex items-start text-sm text-gray-600 dark:text-gray-300">
                      <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default ModuleOverviewSection
